import { useEffect, useState } from "react";
import { toast } from "sonner";
import { adm, apiErr } from "../../lib/adminApi";
import { PageHead, TInput, TArea, Sel, Btn, Card, Badge, MediaPicker } from "../../components/admin/ui";

const credTone = (s) => (s === "ISSUED" ? "gold" : s === "REDEEMED" ? "green" : "red");

export default function AdminDimensionV() {
  const [data, setData] = useState(null);
  const [issuedCode, setIssuedCode] = useState(null);
  const [entEmail, setEntEmail] = useState("");
  const [search, setSearch] = useState("");

  const refresh = () => adm.dvOverview().then(setData).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { refresh(); }, []);
  if (!data) return <p className="text-white/40 font-mono text-xs">LOADING…</p>;

  const { settings, ledger, credentials, entitledUsers, content, fulfillmentStates, policies } = data;

  const savePolicy = async (patch) => {
    try {
      await adm.dvPolicy({ ...settings, ...patch });
      toast.success("Policy saved.");
      refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const setFulfillment = async (orderNumber, status) => {
    try {
      const r = await adm.dvFulfillment(orderNumber, status);
      if (r.autoIssued) { setIssuedCode(r.autoIssued); toast.success("Shipped — credential auto-issued."); }
      else toast.success(`Fulfillment: ${status}`);
      refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const issueCollection = async (email, override = false) => {
    if (!window.confirm(`Issue a Dimension V credential for ${email}?${override ? " (OVERRIDING collection/fulfillment requirements)" : ""}`)) return;
    try {
      const r = await adm.dvIssueCollection({ email, override });
      setIssuedCode(r);
      toast.success("Credential issued — customer notified.");
      refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const revoke = async (id) => {
    const notes = window.prompt("Reason for revocation (logged):");
    if (notes === null) return;
    try { await adm.dvRevoke(id, notes); toast.success("Revoked."); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const reissue = async (id) => {
    const notes = window.prompt("Reason for reissue (logged):");
    if (notes === null) return;
    try { const r = await adm.dvReissue(id, notes); setIssuedCode(r); toast.success("Reissued."); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const entAction = async (email, action) => {
    if (!window.confirm(`${action.toUpperCase()} Dimension V entitlement for ${email}?`)) return;
    try { await adm.dvEntitlement({ email, action, notes: "" }); toast.success(`Entitlement ${action}d.`); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const saveContent = async () => {
    try { await adm.dvContent(content); toast.success("Dimension V content saved."); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const setContent = (patch) => setData((d) => ({ ...d, content: { ...d.content, ...patch } }));
  const setTrack = (i, patch) => setContent({ tracks: content.tracks.map((t, j) => (j === i ? { ...t, ...patch } : t)) });

  const filteredCreds = credentials.filter((c) =>
    !search || [c.orderNumber, c.customerEmail, c.redeemedByEmail, c.codeHint]
      .some((v) => (v || "").toLowerCase().includes(search.toLowerCase())));

  return (
    <div data-testid="admin-dimension-v">
      <PageHead title="Dimension V" sub="Credential issuance, redemption oversight and the Fifth Dimension experience. All actions are audited." />

      {issuedCode && (
        <Card className="mb-6 border-gold/40" >
          <p className="font-mono text-[10px] tracking-[0.3em] text-gold mb-3">CREDENTIAL ISSUED — RECORD NOW FOR PHYSICAL CARD PRINTING</p>
          <div className="flex flex-wrap items-center gap-4">
            <code className="font-mono text-base tracking-[0.12em] text-gold border border-gold/40 bg-black/50 px-5 py-3" data-testid="dv-issued-code">{issuedCode.code}</code>
            <span className="font-mono text-[9px] text-white/40">{issuedCode.orderNumber} · {issuedCode.customerEmail}</span>
            <Btn variant="ghost" onClick={() => { navigator.clipboard.writeText(issuedCode.code); toast.success("Copied."); }} testid="dv-issued-copy">COPY</Btn>
            <Btn variant="ghost" onClick={() => setIssuedCode(null)} testid="dv-issued-dismiss">DISMISS</Btn>
          </div>
          <p className="font-mono text-[8px] text-white/30 mt-3">The purchaser can also view this credential in their authenticated account. It is never emailed in plain text.</p>
        </Card>
      )}

      {/* Policy */}
      <Card className="mb-6">
        <p className="font-mono text-[10px] tracking-[0.3em] text-gold mb-4">ISSUANCE POLICY</p>
        <div className="grid md:grid-cols-2 gap-4">
          <Sel label="POLICY" value={settings.issuancePolicy} onChange={(v) => savePolicy({ issuancePolicy: v })}
            options={policies.map((p) => ({ v: p, l: p.replace(/_/g, " ") }))} testid="dv-policy" />
          <Sel label="REQUIRED FULFILLMENT BEFORE ISSUE" value={settings.requiredFulfillmentStatus}
            onChange={(v) => savePolicy({ requiredFulfillmentStatus: v })}
            options={fulfillmentStates.map((s) => ({ v: s, l: s.replace(/_/g, " ") }))} testid="dv-required-fulfillment" />
        </div>
        <p className="font-mono text-[8px] text-white/30 mt-3">MANUAL: you verify and issue from this panel. AUTO ON SHIPPED: marking a qualifying order SHIPPED issues automatically.</p>
      </Card>

      {/* Multidimensional Collection Ledger */}
      <Card className="mb-6">
        <p className="font-mono text-[10px] tracking-[0.3em] text-gold mb-2">MULTIDIMENSIONAL COLLECTION LEDGER ({(ledger || []).length})</p>
        <p className="font-mono text-[8px] text-white/30 mb-4 max-w-2xl leading-relaxed">
          DIMENSION V REQUIRES VERIFIED OWNERSHIP OF ALL FOUR PHYSICAL DIMENSIONS (I–IV) — VIA THE COMPLETE SET OR SEPARATE ORDERS. ONE CREDENTIAL PER CUSTOMER, NEVER DUPLICATED.
        </p>
        {(ledger || []).length === 0 && <p className="text-white/35 text-sm" data-testid="dv-no-ledger">No qualifying purchases yet.</p>}
        {(ledger || []).map((r) => (
          <div key={r.email} className="border-t border-white/10 py-4" data-testid={`dv-ledger-${r.email}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-white/85 text-sm truncate">{r.email}</p>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {["01", "02", "03", "04"].map((d) => {
                    const s = (r.dimensions || {})[d] || {};
                    return (
                      <span key={d} data-testid={`dv-ledger-${r.email}-dim-${d}`}
                        title={s.fulfilled ? "Owned & fulfilled" : s.owned ? "Owned — awaiting fulfillment" : "Not owned"}
                        className={`font-mono text-[8px] tracking-[0.2em] border px-2.5 py-1 ${
                          s.fulfilled ? "border-gold/60 text-gold" : s.owned ? "border-white/40 text-white/70" : "border-white/10 text-white/25"}`}>
                        {d} {s.fulfilled ? "✓" : s.owned ? "…" : "—"}
                      </span>
                    );
                  })}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                {r.entitlement && (
                  <Badge tone={r.entitlement === "active" ? "green" : r.entitlement === "review" ? "gold" : "red"}>
                    ACCESS {r.entitlement.toUpperCase()}
                  </Badge>
                )}
                {r.credential ? (
                  <Badge tone={credTone(r.credential.status)}>{r.credential.status}{r.credential.underReview ? " · REVIEW" : ""}</Badge>
                ) : ["active", "review"].includes(r.entitlement) ? null : (
                  <>
                    {!r.eligible && <Badge tone="dim">{r.ownedAll ? "AWAITING FULFILLMENT" : "INCOMPLETE"}</Badge>}
                    {r.eligible && <Btn onClick={() => issueCollection(r.email)} testid={`dv-ledger-${r.email}-issue`}>ISSUE</Btn>}
                    <Btn variant="ghost" onClick={() => issueCollection(r.email, true)} testid={`dv-ledger-${r.email}-override`}>OVERRIDE</Btn>
                  </>
                )}
              </div>
            </div>
            <div className="mt-3 space-y-2">
              {(r.orders || []).map((o) => (
                <div key={o.order_number} className="grid md:grid-cols-12 gap-2 items-center">
                  <p className="font-mono text-[9px] text-white/50 md:col-span-3">{o.order_number} · GRANTS {o.grants.join(", ") || "—"}</p>
                  <p className="font-mono text-[8px] text-white/30 md:col-span-5 truncate">{o.items.join(", ")}</p>
                  <div className="md:col-span-4">
                    <Sel label="" value={o.fulfillmentStatus} onChange={(v) => setFulfillment(o.order_number, v)}
                      options={fulfillmentStates.map((s) => ({ v: s, l: s.replace(/_/g, " ") }))} testid={`dv-order-${o.order_number}-fulfillment`} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </Card>

      {/* Credentials */}
      <Card className="mb-6">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <p className="font-mono text-[10px] tracking-[0.3em] text-gold">CREDENTIALS ({credentials.length})</p>
          <TInput label="" value={search} onChange={setSearch} placeholder="Search order / email / hint…" testid="dv-cred-search" />
        </div>
        {filteredCreds.length === 0 && <p className="text-white/35 text-sm">No credentials.</p>}
        {filteredCreds.map((c) => (
          <div key={c.id} className="border-t border-white/10 py-4 grid md:grid-cols-12 gap-3 items-center" data-testid={`dv-cred-${c.codeHint}`}>
            <div className="md:col-span-3">
              <p className="font-mono text-[11px] text-white/85">····{c.codeHint} {c.physicalCard && <Badge tone="dim">CARD</Badge>}</p>
              <p className="font-mono text-[8px] text-white/35 mt-1">{c.orderNumber} · ISSUED {c.issuedAt?.slice(0, 10)} BY {c.issuedBy}</p>
            </div>
            <div className="md:col-span-3">
              <p className="text-white/55 text-xs truncate">{c.customerEmail}</p>
              {c.redeemedByEmail && <p className="font-mono text-[8px] text-white/35 mt-1">REDEEMED BY {c.redeemedByEmail} · {c.redeemedAt?.slice(0, 10)}</p>}
            </div>
            <div className="md:col-span-3"><Badge tone={credTone(c.status)}>{c.status}{c.underReview ? " · REVIEW" : ""}</Badge></div>
            <div className="md:col-span-3 flex gap-2 justify-end">
              {c.status !== "REVOKED" && <Btn variant="danger" onClick={() => revoke(c.id)} testid={`dv-cred-${c.codeHint}-revoke`}>REVOKE</Btn>}
              <Btn variant="ghost" onClick={() => reissue(c.id)} testid={`dv-cred-${c.codeHint}-reissue`}>REISSUE</Btn>
            </div>
          </div>
        ))}
      </Card>

      {/* Entitlements */}
      <Card className="mb-6">
        <p className="font-mono text-[10px] tracking-[0.3em] text-gold mb-4">ACCOUNT ENTITLEMENTS ({entitledUsers.length})</p>
        {entitledUsers.map((u) => (
          <div key={u.email} className="border-t border-white/10 py-3 flex flex-wrap items-center justify-between gap-3" data-testid={`dv-ent-${u.email}`}>
            <div>
              <p className="text-white/80 text-sm">{u.email}</p>
              <p className="font-mono text-[8px] text-white/35 mt-0.5">{u.redeemedAt ? `SINCE ${u.redeemedAt.slice(0, 10)}` : ""}</p>
            </div>
            <div className="flex items-center gap-3">
              <Badge tone={u.status === "active" ? "green" : u.status === "review" ? "gold" : "red"}>{(u.status || "").toUpperCase()}</Badge>
              {u.status === "active" && <Btn variant="ghost" onClick={() => entAction(u.email, "review")} testid={`dv-ent-${u.email}-review`}>REVIEW</Btn>}
              {u.status !== "active" && <Btn variant="ghost" onClick={() => entAction(u.email, "restore")} testid={`dv-ent-${u.email}-restore`}>RESTORE</Btn>}
              {u.status !== "revoked" && <Btn variant="danger" onClick={() => entAction(u.email, "revoke")} testid={`dv-ent-${u.email}-revoke`}>REVOKE</Btn>}
            </div>
          </div>
        ))}
        <div className="flex items-end gap-3 mt-5 border-t border-white/10 pt-5">
          <TInput label="ACT ON ACCOUNT BY EMAIL" value={entEmail} onChange={setEntEmail} placeholder="fan@example.com" testid="dv-ent-email" />
          <Btn variant="ghost" onClick={() => entEmail && entAction(entEmail, "review")}>REVIEW</Btn>
          <Btn variant="ghost" onClick={() => entEmail && entAction(entEmail, "restore")}>RESTORE</Btn>
        </div>
      </Card>

      {/* Content */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <p className="font-mono text-[10px] tracking-[0.3em] text-gold">DIMENSION V EXPERIENCE — STREAMING ONLY</p>
          <Btn onClick={saveContent} testid="dv-content-save">SAVE CONTENT</Btn>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <TInput label="TITLE" value={content.title} onChange={(v) => setContent({ title: v })} testid="dv-content-title" />
          <MediaPicker label="ARTWORK (URL)" value={content.artwork} onChange={(v) => setContent({ artwork: v || null })} mode="url" filterType="image" />
          <div />
          <TArea label="STATEMENT" value={content.statement} onChange={(v) => setContent({ statement: v })} rows={3} className="md:col-span-3" testid="dv-content-statement" />
        </div>
        <div className="flex items-center justify-between mt-6 mb-3">
          <p className="font-mono text-[9px] tracking-[0.25em] text-white/45">TRACKS — USE PROTECTED (RESTRICTED) MEDIA ONLY. NEVER PUBLIC ASSETS.</p>
          <Btn variant="ghost" onClick={() => setContent({ tracks: [...(content.tracks || []), { title: "", mediaId: null, duration: "" }] })} testid="dv-content-add-track">ADD TRACK</Btn>
        </div>
        {(content.tracks || []).map((t, i) => (
          <div key={i} className="grid md:grid-cols-12 gap-3 items-end border-t border-white/10 py-3">
            <div className="md:col-span-4"><TInput label="TITLE" value={t.title} onChange={(v) => setTrack(i, { title: v })} testid={`dv-track-${i}-title`} /></div>
            <div className="md:col-span-5"><MediaPicker label="PROTECTED AUDIO" value={t.mediaId} onChange={(v) => setTrack(i, { mediaId: v || null })} mode="id" filterType="audio" testid={`dv-track-${i}-media`} /></div>
            <div className="md:col-span-2"><TInput label="DURATION" value={t.duration} onChange={(v) => setTrack(i, { duration: v })} placeholder="4:32" /></div>
            <div className="md:col-span-1 pb-1">
              <button onClick={() => setContent({ tracks: content.tracks.filter((_, j) => j !== i) })} className="text-red-400/70 hover:text-red-400 text-xs">✕</button>
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
}
